// Cell registry — keyed by ColumnType. The Cell dispatcher in <Cell /> drives
// every table/calendar/mobile-card render path.

import type { PillStyle } from "@/components/ui/StatusPill";
import { isWorkflowKind, ticketLabel } from "@/lib/boardView";
import type { ViewColumn, ViewRow } from "@/lib/boardView";
import { isWorkflowStatusColumn } from "@/lib/statusRoles";
import type { BoardKind, CellValue, ColumnDef, FileRef, LeadTimeValue, UUID } from "@/lib/types";
import type { LinkValue } from "@/components/board/cells/LinkCell";
import { LeadTimeCell } from "@/components/board/cells/LeadTimeCell";

import { TextCell } from "@/components/board/cells/TextCell";
import { TitleCell } from "@/components/board/cells/TitleCell";
import { LongTextCell } from "@/components/board/cells/LongTextCell";
import { StatusCell } from "@/components/board/cells/StatusCell";
import { PersonCell } from "@/components/board/cells/PersonCell";
import { DateCell } from "@/components/board/cells/DateCell";
import { TimelineCell } from "@/components/board/cells/TimelineCell";
import { NumberCell } from "@/components/board/cells/NumberCell";
import { CheckboxCell } from "@/components/board/cells/CheckboxCell";
import { TagsCell } from "@/components/board/cells/TagsCell";
import { LinkCell } from "@/components/board/cells/LinkCell";
import { FileCell } from "@/components/board/cells/FileCell";

export {
  TextCell,
  TitleCell,
  LongTextCell,
  StatusCell,
  PersonCell,
  DateCell,
  TimelineCell,
  NumberCell,
  CheckboxCell,
  TagsCell,
  LinkCell,
  FileCell,
  LeadTimeCell,
};

interface CellProps {
  column: ViewColumn;
  row: ViewRow;
  rowH?: number;
  pillStyle?: PillStyle;
  sticky?: boolean;
  workspaceId?: string | null;
  /** Board kind drives per-kind cell behavior — currently: status is read-only
   * on ticket boards (workflow actions own status transitions). */
  boardKind?: BoardKind;
  /** Board goal/target date (Zieltermin) — lead_time cells derive their
   * deadline from it. */
  boardGoalDate?: string | null;
  /** Board id — required for the title-cell inline-create flow. */
  boardId?: string | null;
  /** Ticketing: per-board ID prefix (e.g. "IT"), for the title-cell ID chip. */
  ticketPrefix?: string | null;
  onPatchRow: (id: string, patch: Partial<ViewRow>) => void;
  onRowClick?: (row: ViewRow) => void;
  /** Title-cell only: called when Enter is pressed in edit-mode. Parent
   * mints a new row directly after `afterRowId` and focuses its title. */
  onCreateNext?: (afterRowId: UUID) => void;
  /** Generic per-cell text write for non-title text columns. Required for
   * inline-edit on text columns other than the first sticky one. */
  onPatchCell?: (rowId: string, columnDefId: string, value: CellValue) => void;
  /** Board-admin-only inline-create for status + tags column options. */
  onAddColumnOption?: (
    column: ColumnDef,
    label: string,
  ) => Promise<{ id: string; label: string; color: string } | null>;
  canAddColumnOption?: boolean;
  /** Title-cell only: start in edit mode with the cursor focused — for a row
   * that was just created via "+ Zeile" / Enter / "+ Untereintrag". */
  autoFocus?: boolean;
}

export function Cell({
  column,
  row,
  rowH,
  pillStyle,
  sticky,
  workspaceId,
  boardKind,
  boardGoalDate,
  boardId,
  ticketPrefix,
  onPatchRow,
  onRowClick,
  onCreateNext,
  onPatchCell,
  onAddColumnOption,
  canAddColumnOption = false,
  autoFocus = false,
}: CellProps) {
  switch (column.type) {
    case "text": {
      if (sticky) {
        const ticketing = isWorkflowKind(boardKind as BoardKind);
        return (
          <TitleCell
            value={row.title}
            rowH={rowH ?? 44}
            rowId={row.id as UUID}
            boardId={(boardId ?? "") as UUID}
            columnDefId={column.id as UUID}
            ticketIdLabel={ticketing ? ticketLabel(ticketPrefix ?? null, row.ticketNumber) : null}
            ticketCreatedAt={ticketing ? (row.raw?.created_at ?? null) : null}
            ticketResolvedAt={ticketing ? (row.raw?.resolved_at ?? null) : null}
            commentCount={row.commentCount}
            isSubitem={(row.depth ?? 0) > 0}
            autoFocus={autoFocus}
            onOpen={onRowClick ? () => onRowClick(row) : () => {}}
            onPatch={(v) => onPatchRow(row.id, { title: v })}
            onCreateNext={(afterId) => onCreateNext?.(afterId)}
          />
        );
      }
      const cell = row.raw?.cells?.find((c) => c.column_def_id === column.id);
      const rawVal = cell?.value;
      const value = typeof rawVal === "string" ? rawVal : "";
      const patch = onPatchCell ? (v: string) => onPatchCell(row.id, column.id, v) : undefined;
      return (
        <TextCell
          value={value}
          rowH={rowH ?? 44}
          sticky={false}
          openable={false}
          {...(patch ? { onPatch: patch } : {})}
        />
      );
    }
    case "long_text":
      return (
        <LongTextCell
          value={row.notes}
          rowH={rowH ?? 44}
          onPatch={(v) => onPatchRow(row.id, { notes: v })}
        />
      );
    case "status": {
      const rawColumn = column.raw as ColumnDef | undefined;
      const addOption =
        onAddColumnOption && rawColumn
          ? (label: string) => onAddColumnOption(rawColumn, label)
          : undefined;
      // Column-scoped (like checkbox/lead_time): a ticket board has SEVERAL
      // status columns (workflow Status + Bereich + Priorität). The canonical
      // `row.status` only carries the FIRST status column, so a second one
      // (Bereich) rendered the wrong/blank value in the table. Read + write by
      // the real column.id; fall back to the canonical field only for fixtures
      // (no row.raw / no onPatchCell). KanbanView already does this.
      const cell = row.raw?.cells?.find((c) => c.column_def_id === column.id);
      const cellVal = cell?.value;
      const value = row.raw ? (typeof cellVal === "string" ? cellVal : null) : row.status;
      return (
        <StatusCell
          value={value}
          options={column.options ?? []}
          rowH={rowH ?? 44}
          pillStyle={pillStyle ?? "tint"}
          readOnly={isWorkflowKind(boardKind as BoardKind) && isWorkflowStatusColumn(column)}
          onChange={(v) =>
            onPatchCell ? onPatchCell(row.id, column.id, v) : onPatchRow(row.id, { status: v })
          }
          {...(addOption ? { onAddOption: addOption } : {})}
          canCreateOption={canAddColumnOption}
        />
      );
    }
    case "person":
      return (
        <PersonCell
          value={row.owners}
          workspaceId={workspaceId ?? null}
          boardId={(boardId ?? null) as UUID | null}
          allowMultiple={(column.raw?.config?.allow_multiple as boolean | undefined) ?? true}
          rowH={rowH ?? 44}
          onChange={(next) => onPatchRow(row.id, { owners: next })}
        />
      );
    case "date": {
      // Column-scoped (like checkbox/status/lead_time): a board can have SEVERAL date
      // columns ("Deadline" + "Erledigt am" on Aktionen). The canonical `row.due` only
      // carries the FIRST one, so every date column showed that date and an edit in
      // any of them overwrote the first. Read + write by the real column.id; fall back
      // to the canonical field only for fixtures (no row.raw / no onPatchCell).
      const cell = row.raw?.cells?.find((c) => c.column_def_id === column.id);
      const cellVal = cell?.value;
      const value = row.raw ? (typeof cellVal === "string" ? cellVal.slice(0, 10) : null) : row.due;
      // Only the deadline column (the one `due` maps to) is a deadline. Another date
      // column ("Erledigt am") in the past is just a date, never "überfällig" — so it
      // renders plain (done=true suppresses the overdue/today flags).
      const isDeadline = !row.raw || column.id === row.dueColumnId;
      return (
        <DateCell
          value={value}
          rowH={rowH ?? 44}
          // A resolved ticket or a checked-done task is never "überfällig" — the
          // deadline was met (or its lateness is history), so show a plain date;
          // resolvedAt lets it show a subtle "verspätet" marker if closed late.
          done={isDeadline ? row.done || !!row.resolvedAt : true}
          resolvedAt={isDeadline ? row.resolvedAt : null}
          onPatch={(v) =>
            onPatchCell ? onPatchCell(row.id, column.id, v) : onPatchRow(row.id, { due: v })
          }
        />
      );
    }
    case "timeline":
      return (
        <TimelineCell
          value={row.timeline}
          rowH={rowH ?? 44}
          onPatch={(v) => onPatchRow(row.id, { timeline: v })}
        />
      );
    case "number":
      return (
        <NumberCell
          value={row.budget}
          column={column}
          rowH={rowH ?? 44}
          onPatch={(v) => onPatchRow(row.id, { budget: v })}
        />
      );
    case "tags": {
      const rawColumn = column.raw as ColumnDef | undefined;
      const addOption =
        onAddColumnOption && rawColumn
          ? (label: string) => onAddColumnOption(rawColumn, label)
          : undefined;
      return (
        <TagsCell
          value={row.tags}
          rowH={rowH ?? 44}
          options={column.options}
          onPatch={(next) => onPatchRow(row.id, { tags: next })}
          {...(addOption ? { onAddOption: addOption } : {})}
          canCreateOption={canAddColumnOption}
        />
      );
    }
    case "link":
      return (
        <LinkCell
          value={row.link}
          rowH={rowH ?? 44}
          onPatch={(v: LinkValue | null) => onPatchRow(row.id, { link: v })}
        />
      );
    case "checkbox": {
      // Column-scoped (like lead_time): a board can have several checkbox
      // columns ("Geprüft von MFI" …). Reading/writing the canonical `done`
      // field collapsed them all onto the FIRST checkbox column, so the table
      // never reflected a detail-view edit on any other one. Read + write by
      // the real column.id; fall back to the canonical field only for fixtures
      // (no row.raw / no onPatchCell).
      const cell = row.raw?.cells?.find((c) => c.column_def_id === column.id);
      const checked = row.raw ? cell?.value === true : row.done;
      return (
        <CheckboxCell
          value={checked}
          rowH={rowH ?? 44}
          onChange={(v) =>
            onPatchCell ? onPatchCell(row.id, column.id, v) : onPatchRow(row.id, { done: v })
          }
        />
      );
    }
    case "file": {
      const cell = row.raw?.cells?.find((c) => c.column_def_id === column.id);
      const refs = Array.isArray(cell?.value) ? (cell.value as FileRef[]) : [];
      return (
        <FileCell
          value={refs}
          rowH={rowH ?? 44}
          {...(onPatchCell ? { onPatch: (v: FileRef[]) => onPatchCell(row.id, column.id, v) } : {})}
        />
      );
    }
    case "lead_time": {
      const cell = row.raw?.cells?.find((c) => c.column_def_id === column.id);
      const raw = cell?.value;
      const value =
        raw && typeof raw === "object" && !Array.isArray(raw) && "unit" in raw && "value" in raw
          ? (raw as LeadTimeValue)
          : null;
      return (
        <LeadTimeCell
          value={value}
          goalDate={boardGoalDate ?? null}
          rowH={rowH ?? 44}
          {...(onPatchCell
            ? { onPatch: (v: LeadTimeValue | null) => onPatchCell(row.id, column.id, v) }
            : {})}
        />
      );
    }
    case "creator":
      // Read-only: who created the ticket (computed from created_by).
      return (
        <TextCell
          value={row.raw?.created_by_display_name ?? row.raw?.created_by_username ?? "—"}
          rowH={rowH ?? 44}
          sticky={false}
          openable={false}
        />
      );
    case "creator_department":
      // Read-only: requester's home department, snapshotted at creation.
      return (
        <TextCell
          value={row.raw?.creator_department_name ?? "—"}
          rowH={rowH ?? 44}
          sticky={false}
          openable={false}
        />
      );
    case "creator_company":
      // Read-only: requester's company (workspace.company), snapshotted at creation.
      return (
        <TextCell
          value={row.raw?.creator_company_name ?? "—"}
          rowH={rowH ?? 44}
          sticky={false}
          openable={false}
        />
      );
    default:
      return null;
  }
}

export type { CellProps };
